import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import Header from "../components/Header";
import { FilterPanel } from "../components/revenue/FilterPanel";
import { Search } from 'lucide-react';
import { useData } from "../context/DataContext";
import { useCacheCheck } from "../hooks/useCacheCheck";
import { CacheStatus } from "../components/CacheStatus";
import { ExportButton } from "../components/ExportButton";
import { supabase } from "../lib/supabase";

type SortConfig = {
  key: string;
  direction: 'asc' | 'desc';
};

function formatCurrency(amount: number): string {
  const formattedNumber = Math.round(amount)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${formattedNumber}€`;
}

const HOTEL_ID_BATCH = 400;

function formatHotelCreatedLabel(raw: string | null | undefined): string {
  if (raw == null || raw === '') return '—';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

function parseHotelCreatedSort(raw: string | null | undefined): number {
  if (raw == null || raw === '') return 0;
  const t = new Date(raw).getTime();
  return Number.isNaN(t) ? 0 : t;
}

type HotelCreationPeriod = 'this_month' | 'this_year' | 'last_year' | 'all_time' | 'custom';

function endOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

function getHotelCreationDateRange(
  period: HotelCreationPeriod,
  customFrom: string,
  customTo: string,
): { start: Date; end: Date } | null {
  const now = new Date();

  if (period === 'all_time') return null;

  if (period === 'this_month') {
    return {
      start: new Date(now.getFullYear(), now.getMonth(), 1),
      end: endOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    };
  }

  if (period === 'this_year') {
    return {
      start: new Date(now.getFullYear(), 0, 1),
      end: endOfDay(new Date(now.getFullYear(), 11, 31)),
    };
  }

  if (period === 'last_year') {
    const y = now.getFullYear() - 1;
    return {
      start: new Date(y, 0, 1),
      end: endOfDay(new Date(y, 11, 31)),
    };
  }

  const start = customFrom ? startOfDay(new Date(customFrom)) : new Date(0);
  const end = customTo ? endOfDay(new Date(customTo)) : endOfDay(now);
  return { start, end };
}

function isHotelCreatedInRange(
  raw: string | null | undefined,
  range: { start: Date; end: Date } | null,
): boolean {
  if (range === null) return true;
  if (raw == null || raw === '') return false;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return false;
  return d >= range.start && d <= range.end;
}

function defaultCustomFromDate(): string {
  const now = new Date();
  return `${now.getFullYear()}-01-01`;
}

function defaultCustomToDate(): string {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/** Same color tiers as Materialization per Client / City (ClientHotelMaterialization). */
function materializationBadgeClass(pct: number): string {
  if (pct === 0) return 'bg-red-100 text-red-800';
  if (pct > 0 && pct <= 20) return 'bg-yellow-100 text-yellow-800';
  return 'bg-green-100 text-green-800';
}

function formatMaterializationPct(pct: number): string {
  return `${Math.round(pct)}%`;
}

type HotelAgg = {
  rowKey: string;
  venue: string;
  hotelGroupLabel: string;
  hotelId: number | null;
  bookedX: number;
  bookedY: number;
  cancelledX: number;
  cancelledY: number;
  estX: number;
  estY: number;
  realX: number;
  realY: number;
  diffEst: number;
  diffReal: number;
  materializationPct: number | null;
  dateSort: number;
};

type CatalogHotel = {
  id: number;
  title: string;
  date_created: string | null;
  hotel_group_name: string | null;
};

function isAllSelected<T>(selected: Set<T>, allOptions: T[]) {
  return selected.size === 0 || selected.size === allOptions.length;
}

/** Filter label for hotels with no `hotel_group_name` in the database. */
const INDIVIDUAL_HOTEL_GROUP = 'Individual';

function displayHotelGroupName(raw: string | null | undefined): string {
  const name = raw == null ? '' : String(raw).trim();
  return name === '' ? INDIVIDUAL_HOTEL_GROUP : name;
}

/** Percent widths for table-fixed; sum = 100%. Cancelled columns wider than GROUPS (longer labels). */
const nameColumnWidth = '14.5%';
const dateColumnWidth = '7.5%';
const groupsColumnWidth = '4%';
const cancelledColumnWidth = '7.5%';
const revenueColumnWidth = '10%';
const diffColumnWidth = '5.5%';
const pctColumnWidth = '4%';

const thClass =
  'px-1.5 py-2 text-center text-[10px] font-medium uppercase leading-tight tracking-wide text-gray-500 cursor-pointer hover:bg-gray-100 sm:text-[11px] lg:text-xs';
const thClassLeft =
  'px-1.5 py-2 text-left text-[10px] font-medium uppercase leading-tight tracking-wide text-gray-500 cursor-pointer hover:bg-gray-100 sm:text-[11px] lg:text-xs';

const tdNum =
  'px-1.5 py-1.5 text-[11px] tabular-nums whitespace-nowrap lg:text-xs';
const tdHotel =
  'px-1.5 py-2 align-top text-[11px] font-medium leading-snug text-gray-900 break-words hyphens-auto [overflow-wrap:anywhere] lg:text-xs';

export default function HotelsPerformance() {
  const [searchTerm, setSearchTerm] = useState('');
  const currentYear = new Date().getFullYear();
  const [yearX, setYearX] = useState<number>(currentYear);
  const [yearY, setYearY] = useState<number>(currentYear - 1);
  const [hotelCreationPeriod, setHotelCreationPeriod] = useState<HotelCreationPeriod>('this_year');
  const [customCreationFrom, setCustomCreationFrom] = useState(defaultCustomFromDate);
  const [customCreationTo, setCustomCreationTo] = useState(defaultCustomToDate);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'venue', direction: 'asc' });

  const { data, loadingProgress } = useData();
  const [hotelRawDateById, setHotelRawDateById] = useState<Map<number, string>>(() => new Map());
  const [hotelGroupById, setHotelGroupById] = useState<Map<number, string>>(() => new Map());
  const [catalogHotels, setCatalogHotels] = useState<CatalogHotel[]>([]);

  useCacheCheck();

  const [availableCreationYears, setAvailableCreationYears] = useState<number[]>([]);
  const [availableArrivalYears, setAvailableArrivalYears] = useState<number[]>([]);
  const [availableManagers, setAvailableManagers] = useState<string[]>([]);
  const [availableSegments, setAvailableSegments] = useState<string[]>([]);
  const [clients, setClients] = useState<string[]>([]);
  const [hotels, setHotels] = useState<string[]>([]);
  const [hotelGroups, setHotelGroups] = useState<string[]>([]);
  const [markets, setMarkets] = useState<string[]>([]);

  const [appliedCreationYears, setAppliedCreationYears] = useState<Set<number>>(new Set());
  const [appliedArrivalYears, setAppliedArrivalYears] = useState<Set<number>>(new Set());
  const [appliedManagers, setAppliedManagers] = useState<Set<string>>(new Set());
  const [appliedSegments, setAppliedSegments] = useState<Set<string>>(new Set());
  const [appliedClients, setAppliedClients] = useState<Set<string>>(new Set());
  const [appliedHotels, setAppliedHotels] = useState<Set<string>>(new Set());
  const [appliedHotelGroups, setAppliedHotelGroups] = useState<Set<string>>(new Set());
  const [appliedArrivalMonths, setAppliedArrivalMonths] = useState<Set<number>>(new Set());
  const [appliedArrivalQuarters, setAppliedArrivalQuarters] = useState<Set<number>>(new Set());
  const [appliedCreationMonths, setAppliedCreationMonths] = useState<Set<number>>(new Set());
  const [appliedCreationQuarters, setAppliedCreationQuarters] = useState<Set<number>>(new Set());
  const [appliedMarkets, setAppliedMarkets] = useState<Set<string>>(new Set());

  const [pendingCreationYears, setPendingCreationYears] = useState<Set<number>>(new Set());
  const [pendingArrivalYears, setPendingArrivalYears] = useState<Set<number>>(new Set());
  const [pendingManagers, setPendingManagers] = useState<Set<string>>(new Set());
  const [pendingSegments, setPendingSegments] = useState<Set<string>>(new Set());
  const [pendingClients, setPendingClients] = useState<Set<string>>(new Set());
  const [pendingHotels, setPendingHotels] = useState<Set<string>>(new Set());
  const [pendingHotelGroups, setPendingHotelGroups] = useState<Set<string>>(new Set());
  const [pendingArrivalMonths, setPendingArrivalMonths] = useState<Set<number>>(new Set());
  const [pendingArrivalQuarters, setPendingArrivalQuarters] = useState<Set<number>>(new Set());
  const [pendingCreationMonths, setPendingCreationMonths] = useState<Set<number>>(new Set());
  const [pendingCreationQuarters, setPendingCreationQuarters] = useState<Set<number>>(new Set());
  const [pendingMarkets, setPendingMarkets] = useState<Set<string>>(new Set());

  const [allClientsSelected, setAllClientsSelected] = useState(true);
  const [allHotelGroupsSelected, setAllHotelGroupsSelected] = useState(true);
  const [allHotelsSelected, setAllHotelsSelected] = useState(true);
  const [allMarketsSelected, setAllMarketsSelected] = useState(true);

  const handleClientsChange = (newSelected: Set<string>) => {
    setPendingClients(newSelected);
    setAllClientsSelected(newSelected.size === 0);
  };

  const handleHotelGroupsChange = (newSelected: Set<string>) => {
    setPendingHotelGroups(newSelected);
    setAllHotelGroupsSelected(newSelected.size === 0);
  };

  const handleHotelsChange = (newSelected: Set<string>) => {
    setPendingHotels(newSelected);
    setAllHotelsSelected(newSelected.size === 0);
  };

  const handleMarketsChange = (newSelected: Set<string>) => {
    setPendingMarkets(newSelected);
    setAllMarketsSelected(newSelected.size === 0);
  };

  const hasUnappliedChanges = useMemo(() => {
    const setsEqual = (a: Set<any>, b: Set<any>) =>
      a.size === b.size && [...a].every(x => b.has(x));

    return !setsEqual(pendingCreationYears, appliedCreationYears) ||
           !setsEqual(pendingArrivalYears, appliedArrivalYears) ||
           !setsEqual(pendingManagers, appliedManagers) ||
           !setsEqual(pendingSegments, appliedSegments) ||
           !setsEqual(pendingClients, appliedClients) ||
           !setsEqual(pendingHotels, appliedHotels) ||
           !setsEqual(pendingHotelGroups, appliedHotelGroups) ||
           !setsEqual(pendingArrivalMonths, appliedArrivalMonths) ||
           !setsEqual(pendingArrivalQuarters, appliedArrivalQuarters) ||
           !setsEqual(pendingCreationMonths, appliedCreationMonths) ||
           !setsEqual(pendingCreationQuarters, appliedCreationQuarters) ||
           !setsEqual(pendingMarkets, appliedMarkets);
  }, [
    pendingCreationYears, appliedCreationYears,
    pendingArrivalYears, appliedArrivalYears,
    pendingManagers, appliedManagers,
    pendingSegments, appliedSegments,
    pendingClients, appliedClients,
    pendingHotels, appliedHotels,
    pendingHotelGroups, appliedHotelGroups,
    pendingArrivalMonths, appliedArrivalMonths,
    pendingArrivalQuarters, appliedArrivalQuarters,
    pendingCreationMonths, appliedCreationMonths,
    pendingCreationQuarters, appliedCreationQuarters,
    pendingMarkets, appliedMarkets,
  ]);

  const handleApplyFilters = () => {
    setAppliedCreationYears(new Set(pendingCreationYears));
    setAppliedArrivalYears(new Set(pendingArrivalYears));
    setAppliedManagers(new Set(pendingManagers));
    setAppliedSegments(new Set(pendingSegments));
    setAppliedClients(new Set(pendingClients));
    setAppliedHotels(new Set(pendingHotels));
    setAppliedHotelGroups(new Set(pendingHotelGroups));
    setAppliedArrivalMonths(new Set(pendingArrivalMonths));
    setAppliedArrivalQuarters(new Set(pendingArrivalQuarters));
    setAppliedCreationMonths(new Set(pendingCreationMonths));
    setAppliedCreationQuarters(new Set(pendingCreationQuarters));
    setAppliedMarkets(new Set(pendingMarkets));
  };

  useEffect(() => {
    if (data && data.length > 0) {
      const clientsList = Array.from(new Set(data.map((row: any) => row.company).filter(Boolean))).sort();
      setClients(clientsList);
    }
  }, [data]);

  useEffect(() => {
    const allMonths = new Set([1,2,3,4,5,6,7,8,9,10,11,12]);
    const allQuarters = new Set([1,2,3,4]);
    setPendingCreationMonths(allMonths);
    setPendingCreationQuarters(allQuarters);
    setPendingArrivalMonths(allMonths);
    setPendingArrivalQuarters(allQuarters);
    setAppliedCreationMonths(allMonths);
    setAppliedCreationQuarters(allQuarters);
    setAppliedArrivalMonths(allMonths);
    setAppliedArrivalQuarters(allQuarters);
  }, []);

  const filtersInitialized = useRef(false);

  useEffect(() => {
    if (filtersInitialized.current) return;
    if (availableManagers.length === 0 || availableSegments.length === 0 ||
        availableCreationYears.length === 0 || availableArrivalYears.length === 0 ||
        clients.length === 0 || hotels.length === 0 || hotelGroups.length === 0 ||
        markets.length === 0) {
      return;
    }

    const allManagers = new Set(availableManagers);
    const allSegments = new Set(availableSegments);
    const allCreationYears = new Set(availableCreationYears);
    const allArrivalYears = new Set(availableArrivalYears);
    const allClients = new Set(clients);
    const allHotelsList = new Set(hotels);
    const allHotelGroups = new Set(hotelGroups);
    const allMarketsList = new Set(markets);

    setPendingManagers(allManagers);
    setPendingSegments(allSegments);
    setPendingClients(allClients);
    setPendingHotels(allHotelsList);
    setPendingHotelGroups(allHotelGroups);
    setPendingMarkets(allMarketsList);
    setPendingCreationYears(allCreationYears);
    setPendingArrivalYears(allArrivalYears);

    setAppliedManagers(allManagers);
    setAppliedSegments(allSegments);
    setAppliedClients(allClients);
    setAppliedHotels(allHotelsList);
    setAppliedHotelGroups(allHotelGroups);
    setAppliedMarkets(allMarketsList);
    setAppliedCreationYears(allCreationYears);
    setAppliedArrivalYears(allArrivalYears);

    setAllClientsSelected(false);
    setAllHotelsSelected(false);
    setAllHotelGroupsSelected(false);
    setAllMarketsSelected(false);

    filtersInitialized.current = true;
  }, [availableManagers, availableSegments, clients, hotels, hotelGroups, markets, availableCreationYears, availableArrivalYears]);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const creationYears = Array.from(new Set(data.map(row => row.request_year).filter(Boolean))).sort((a, b) => b - a);
    const arrivalYears = Array.from(new Set(data.map(row => row.arrival_year).filter(Boolean))).sort((a, b) => b - a);

    const managers = Array.from(new Set(data.map(row => row.request_manager).filter(Boolean))).sort();

    const segments = Array.from(new Set(data.map(row => row.segment).filter(Boolean))).sort();

    const venues = Array.from(new Set(data.map(row => row.venue).filter(Boolean))).sort();

    let groups = Array.from(new Set(data.map(row => row.hotel_group_name).filter(Boolean))).sort();
    const hasNoGroup = data.some(row => !row.hotel_group_name);
    if (hasNoGroup) {
      groups = [...groups, INDIVIDUAL_HOTEL_GROUP];
    }
    setHotelGroups(groups);
    setAllHotelGroupsSelected(true);

    let marketValues = Array.from(new Set(data.map(row => row.market).filter(Boolean))).sort();
    const hasNoMarket = data.some(row => !row.market);
    if (hasNoMarket) {
      marketValues = [...marketValues, 'No Market'];
    }
    setMarkets(marketValues);
    setAllMarketsSelected(true);

    setAvailableCreationYears(creationYears);
    setAvailableArrivalYears(arrivalYears);

    if (arrivalYears.length > 0) {
      const mostRecentYear = arrivalYears[0];
      if (arrivalYears.includes(currentYear)) {
        setYearX(currentYear);
        const previousYear = currentYear - 1;
        if (arrivalYears.includes(previousYear)) {
          setYearY(previousYear);
        } else {
          setYearY(mostRecentYear);
        }
      } else {
        setYearX(mostRecentYear);
        if (arrivalYears.length > 1) {
          setYearY(arrivalYears[1]);
        } else {
          setYearY(mostRecentYear);
        }
      }
    }

    setAvailableManagers(managers);
    setAvailableSegments(segments);
    setHotels(venues);
  }, [data]);

  // Fetch hotels.date_created for all hotel_ids present in dataset
  useEffect(() => {
    if (!data || data.length === 0) return;

    const idSet = new Set<number>();
    data.forEach((row: any) => {
      const id = row.hotel_id;
      if (id != null && id !== '') {
        const n = typeof id === 'number' ? id : Number(id);
        if (!Number.isNaN(n)) idSet.add(n);
      }
    });
    const ids = Array.from(idSet);
    if (ids.length === 0) {
      setHotelRawDateById(new Map());
      setHotelGroupById(new Map());
      return;
    }

    let cancelled = false;

    (async () => {
      const mergedDates = new Map<number, string>();
      const mergedGroups = new Map<number, string>();
      for (let i = 0; i < ids.length; i += HOTEL_ID_BATCH) {
        const chunk = ids.slice(i, i + HOTEL_ID_BATCH);
        const { data: rows, error } = await supabase
          .from('hotels')
          .select('id, date_created, hotel_group_name')
          .in('id', chunk);
        if (error) {
          console.error('HotelsPerformance: hotels metadata fetch', error);
          continue;
        }
        (rows || []).forEach((h: { id: number; date_created: string | null; hotel_group_name: string | null }) => {
          if (h.date_created != null) mergedDates.set(h.id, h.date_created);
          mergedGroups.set(h.id, displayHotelGroupName(h.hotel_group_name));
        });
      }
      if (!cancelled) {
        setHotelRawDateById(mergedDates);
        setHotelGroupById(mergedGroups);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [data]);

  const resetFilters = () => {
    const allManagers = new Set(availableManagers);
    const allSegments = new Set(availableSegments);
    const allCreationYears = new Set(availableCreationYears);
    const allArrivalYears = new Set(availableArrivalYears);
    const allClients = new Set(clients);
    const allHotelsList = new Set(hotels);
    const allHotelGroups = new Set(hotelGroups);
    const allMarketsList = new Set(markets);
    const allMonths = new Set([1,2,3,4,5,6,7,8,9,10,11,12]);
    const allQuarters = new Set([1,2,3,4]);

    setPendingCreationYears(allCreationYears);
    setPendingArrivalYears(allArrivalYears);
    setPendingManagers(allManagers);
    setPendingSegments(allSegments);
    setPendingClients(allClients);
    setPendingHotels(allHotelsList);
    setPendingHotelGroups(allHotelGroups);
    setPendingMarkets(allMarketsList);
    setPendingArrivalMonths(allMonths);
    setPendingArrivalQuarters(allQuarters);
    setPendingCreationMonths(allMonths);
    setPendingCreationQuarters(allQuarters);

    setAppliedCreationYears(allCreationYears);
    setAppliedArrivalYears(allArrivalYears);
    setAppliedManagers(allManagers);
    setAppliedSegments(allSegments);
    setAppliedClients(allClients);
    setAppliedHotels(allHotelsList);
    setAppliedHotelGroups(allHotelGroups);
    setAppliedMarkets(allMarketsList);
    setAppliedArrivalMonths(allMonths);
    setAppliedArrivalQuarters(allQuarters);
    setAppliedCreationMonths(allMonths);
    setAppliedCreationQuarters(allQuarters);

    setAllClientsSelected(false);
    setAllHotelsSelected(false);
    setAllHotelGroupsSelected(false);
    setAllMarketsSelected(false);
  };

  const filteredData = useMemo(() => {
    return data.filter(row => {
      if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        const matchesSearch =
          (row.market && row.market.toLowerCase().includes(lower)) ||
          (row.company && row.company.toLowerCase().includes(lower)) ||
          (row.request_manager && row.request_manager.toLowerCase().includes(lower)) ||
          (row.venue && row.venue.toLowerCase().includes(lower)) ||
          (row.segment && row.segment.toLowerCase().includes(lower));
        if (!matchesSearch) return false;
      }

      if (!isAllSelected(appliedManagers, availableManagers) && !appliedManagers.has(row.request_manager)) return false;
      if (!isAllSelected(appliedClients, clients) && !appliedClients.has(row.company)) return false;
      if (!isAllSelected(appliedArrivalYears, availableArrivalYears) && !appliedArrivalYears.has(row.arrival_year)) return false;
      if (!isAllSelected(appliedCreationYears, availableCreationYears) && !appliedCreationYears.has(row.request_year)) return false;
      if (!isAllSelected(appliedSegments, availableSegments) && !appliedSegments.has(row.segment)) return false;
      const groupName = displayHotelGroupName(row.hotel_group_name);
      if (!isAllSelected(appliedHotelGroups, hotelGroups) && !appliedHotelGroups.has(groupName)) return false;
      if (!isAllSelected(appliedHotels, hotels) && !appliedHotels.has(row.venue)) return false;
      if (!isAllSelected(appliedArrivalMonths, [1,2,3,4,5,6,7,8,9,10,11,12]) && !appliedArrivalMonths.has(row.arrival_month)) return false;
      if (!isAllSelected(appliedArrivalQuarters, [1,2,3,4]) && !appliedArrivalQuarters.has(row.arrival_quarter)) return false;
      if (!isAllSelected(appliedCreationMonths, [1,2,3,4,5,6,7,8,9,10,11,12]) && !appliedCreationMonths.has(row.request_month)) return false;
      if (!isAllSelected(appliedCreationQuarters, [1,2,3,4]) && !appliedCreationQuarters.has(row.request_quarter)) return false;
      const marketName = row.market ? row.market : 'No Market';
      if (!isAllSelected(appliedMarkets, markets) && !appliedMarkets.has(marketName)) return false;

      return true;
    });
  }, [data, searchTerm, appliedManagers, availableManagers, appliedClients, clients, appliedArrivalYears, availableArrivalYears, appliedCreationYears, availableCreationYears, appliedSegments, availableSegments, appliedHotelGroups, hotelGroups, appliedHotels, hotels, appliedArrivalMonths, appliedArrivalQuarters, appliedCreationMonths, appliedCreationQuarters, appliedMarkets, markets]);

  /** Label for rows with no hotel_id: trim venue, else "Unassigned". (Booked rows should always have hotel_id in DB.) */
  function venueFallbackLabel(r: any): string {
    const raw = r.venue;
    if (raw == null) return 'Unassigned';
    const s = String(raw).trim();
    return s === '' ? 'Unassigned' : s;
  }

  /** Prefer hotel_id; otherwise bucket by venue text (e.g. Lost rows missing hotel linkage). */
  function rowKeyFor(r: any): string {
    if (r.hotel_id != null && r.hotel_id !== '') {
      const n = typeof r.hotel_id === 'number' ? r.hotel_id : Number(r.hotel_id);
      if (!Number.isNaN(n)) return `id:${n}`;
    }
    return `v:${venueFallbackLabel(r)}`;
  }

  const hotelCreationDateRange = useMemo(
    () => getHotelCreationDateRange(hotelCreationPeriod, customCreationFrom, customCreationTo),
    [hotelCreationPeriod, customCreationFrom, customCreationTo],
  );

  const catalogHotelIdSet = useMemo(
    () => new Set(catalogHotels.map((h) => h.id)),
    [catalogHotels],
  );

  const catalogHotelById = useMemo(
    () => new Map(catalogHotels.map((h) => [h.id, h])),
    [catalogHotels],
  );

  // Include hotels from the catalog when group or hotel name filters narrow the scope.
  useEffect(() => {
    const groupFilterActive = !isAllSelected(appliedHotelGroups, hotelGroups);
    const hotelFilterActive = !isAllSelected(appliedHotels, hotels);

    if (!groupFilterActive && !hotelFilterActive) {
      setCatalogHotels([]);
      return;
    }

    let cancelled = false;

    (async () => {
      let query = supabase.from('hotels').select('id, title, date_created, hotel_group_name');

      if (groupFilterActive) {
        const selected = Array.from(appliedHotelGroups);
        const namedGroups = selected.filter((g) => g !== INDIVIDUAL_HOTEL_GROUP);
        const includeIndividual = selected.includes(INDIVIDUAL_HOTEL_GROUP);

        if (namedGroups.length > 0 && includeIndividual) {
          const groupList = namedGroups.map((g) => `"${g.replace(/"/g, '\\"')}"`).join(',');
          query = query.or(`hotel_group_name.in.(${groupList}),hotel_group_name.is.null`);
        } else if (namedGroups.length > 0) {
          query = query.in('hotel_group_name', namedGroups);
        } else if (includeIndividual) {
          query = query.is('hotel_group_name', null);
        }
      }

      if (hotelFilterActive) {
        query = query.in('title', Array.from(appliedHotels));
      }

      if (hotelCreationDateRange) {
        query = query
          .gte('date_created', hotelCreationDateRange.start.toISOString())
          .lte('date_created', hotelCreationDateRange.end.toISOString());
      }

      const { data: rows, error } = await query.order('title');
      if (cancelled) return;

      if (error) {
        console.error('HotelsPerformance: catalog hotels fetch', error);
        setCatalogHotels([]);
        return;
      }

      const fetched: CatalogHotel[] = (rows || []).map((r) => ({
        id: r.id,
        title: r.title,
        date_created: r.date_created,
        hotel_group_name: r.hotel_group_name,
      }));
      setCatalogHotels(fetched);

      setHotelRawDateById((prev) => {
        const next = new Map(prev);
        fetched.forEach((h) => {
          if (h.date_created != null) next.set(h.id, h.date_created);
        });
        return next;
      });

      setHotelGroupById((prev) => {
        const next = new Map(prev);
        fetched.forEach((h) => {
          next.set(h.id, displayHotelGroupName(h.hotel_group_name));
        });
        return next;
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [
    appliedHotelGroups,
    appliedHotels,
    hotelGroups,
    hotels,
    hotelCreationDateRange,
  ]);

  const flatData = useMemo(() => {
    const resolveRawDate = (hotelId: number | null): string | undefined => {
      if (hotelId == null) return undefined;
      return hotelRawDateById.get(hotelId) ?? catalogHotelById.get(hotelId)?.date_created ?? undefined;
    };

    const matchesSearch = (venue: string): boolean => {
      if (!searchTerm) return true;
      return venue.toLowerCase().includes(searchTerm.toLowerCase());
    };
    const buckets = new Map<string, {
      venue: string;
      hotelGroupLabel: string;
      hotelId: number | null;
      bookedX: number;
      bookedY: number;
      cancelledX: number;
      cancelledY: number;
      estX: number;
      estY: number;
      realX: number;
      realY: number;
    }>();

    filteredData.forEach((row: any) => {
      const key = rowKeyFor(row);
      if (!buckets.has(key)) {
        let hid: number | null = null;
        if (row.hotel_id != null && row.hotel_id !== '') {
          const n = typeof row.hotel_id === 'number' ? row.hotel_id : Number(row.hotel_id);
          if (!Number.isNaN(n)) hid = n;
        }
        buckets.set(key, {
          venue:
            hid != null
              ? String(row.venue ?? '').trim() || `Hotel #${hid}`
              : venueFallbackLabel(row),
          hotelGroupLabel: displayHotelGroupName(row.hotel_group_name),
          hotelId: hid,
          bookedX: 0,
          bookedY: 0,
          cancelledX: 0,
          cancelledY: 0,
          estX: 0,
          estY: 0,
          realX: 0,
          realY: 0,
        });
      }
      const b = buckets.get(key)!;
      const ay = row.arrival_year;

      if (row.status === 'Booked') {
        if (ay === yearX) {
          b.bookedX += 1;
          b.estX += Number(row.estimated_revenue) || 0;
          b.realX += Number(row.realised_revenue) || 0;
        }
        if (ay === yearY) {
          b.bookedY += 1;
          b.estY += Number(row.estimated_revenue) || 0;
          b.realY += Number(row.realised_revenue) || 0;
        }
      }
      if (row.status === 'Lost') {
        if (ay === yearX) b.cancelledX += 1;
        if (ay === yearY) b.cancelledY += 1;
      }
    });

    const out: HotelAgg[] = [];

    buckets.forEach((b, rowKey) => {
      const diffEst = b.estX - b.estY;
      const diffReal = b.realX - b.realY;
      const estSum = b.estX + b.estY;
      const realSum = b.realX + b.realY;
      const materializationPct = estSum > 0 ? (realSum / estSum) * 100 : null;

      const rawDate = resolveRawDate(b.hotelId);
      const inCreationRange = isHotelCreatedInRange(rawDate, hotelCreationDateRange);
      const inCatalog = b.hotelId != null && catalogHotelIdSet.has(b.hotelId);

      if (hotelCreationDateRange !== null && !inCreationRange) return;

      const hasActivity =
        b.bookedX !== 0 || b.bookedY !== 0 ||
        b.cancelledX !== 0 || b.cancelledY !== 0 ||
        b.estX !== 0 || b.estY !== 0 || b.realX !== 0 || b.realY !== 0;
      if (hotelCreationDateRange === null && !hasActivity && !inCatalog) return;

      if (!matchesSearch(b.venue)) return;

      const hotelGroupLabel =
        b.hotelId != null
          ? hotelGroupById.get(b.hotelId) ?? b.hotelGroupLabel
          : b.hotelGroupLabel;

      out.push({
        rowKey,
        venue: b.venue,
        hotelGroupLabel,
        hotelId: b.hotelId,
        bookedX: b.bookedX,
        bookedY: b.bookedY,
        cancelledX: b.cancelledX,
        cancelledY: b.cancelledY,
        estX: b.estX,
        estY: b.estY,
        realX: b.realX,
        realY: b.realY,
        diffEst,
        diffReal,
        materializationPct,
        dateSort: parseHotelCreatedSort(rawDate),
      });
    });

    const seenKeys = new Set(out.map((row) => row.rowKey));
    catalogHotels.forEach((h) => {
      const rowKey = `id:${h.id}`;
      if (seenKeys.has(rowKey)) return;

      const rawDate = h.date_created ?? undefined;
      if (hotelCreationDateRange !== null && !isHotelCreatedInRange(rawDate, hotelCreationDateRange)) return;
      if (!matchesSearch(h.title)) return;

      out.push({
        rowKey,
        venue: h.title,
        hotelGroupLabel: displayHotelGroupName(h.hotel_group_name),
        hotelId: h.id,
        bookedX: 0,
        bookedY: 0,
        cancelledX: 0,
        cancelledY: 0,
        estX: 0,
        estY: 0,
        realX: 0,
        realY: 0,
        diffEst: 0,
        diffReal: 0,
        materializationPct: null,
        dateSort: parseHotelCreatedSort(rawDate),
      });
    });

    return out;
  }, [
    filteredData,
    yearX,
    yearY,
    hotelRawDateById,
    hotelGroupById,
    hotelCreationDateRange,
    catalogHotels,
    catalogHotelIdSet,
    catalogHotelById,
    searchTerm,
  ]);

  const handleSort = (key: string) => {
    setSortConfig(current => ({
      key,
      direction: current?.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortData = useCallback((rows: HotelAgg[]) => {
    return [...rows].sort((a, b) => {
      let aValue: any = (a as any)[sortConfig.key];
      let bValue: any = (b as any)[sortConfig.key];

      if (sortConfig.key === 'createDateDisplay') {
        aValue = a.dateSort;
        bValue = b.dateSort;
      }

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aString = String(aValue).toLowerCase();
      const bString = String(bValue).toLowerCase();
      return sortConfig.direction === 'asc'
        ? aString.localeCompare(bString)
        : bString.localeCompare(aString);
    });
  }, [sortConfig]);

  const sortedData = useMemo(() => sortData(flatData), [flatData, sortData]);

  const totals = useMemo(() => {
    return sortedData.reduce((acc, row) => {
      acc.bookedX += row.bookedX;
      acc.bookedY += row.bookedY;
      acc.cancelledX += row.cancelledX;
      acc.cancelledY += row.cancelledY;
      acc.estX += row.estX;
      acc.estY += row.estY;
      acc.realX += row.realX;
      acc.realY += row.realY;
      acc.diffEst += row.diffEst;
      acc.diffReal += row.diffReal;
      return acc;
    }, {
      bookedX: 0,
      bookedY: 0,
      cancelledX: 0,
      cancelledY: 0,
      estX: 0,
      estY: 0,
      realX: 0,
      realY: 0,
      diffEst: 0,
      diffReal: 0,
    });
  }, [sortedData]);

  const totalMaterializationPct = useMemo(() => {
    const estSum = totals.estX + totals.estY;
    const realSum = totals.realX + totals.realY;
    if (estSum <= 0) return null;
    return (realSum / estSum) * 100;
  }, [totals]);

  const getTableDataForExport = () => {
    const tableData = sortedData.map((row) => {
      const rawDate = row.hotelId != null ? hotelRawDateById.get(row.hotelId) : undefined;
      return {
        Hotel: row.venue,
        'Create Date': formatHotelCreatedLabel(rawDate),
        [`GROUPS ${yearX}`]: row.bookedX,
        [`GROUPS ${yearY}`]: row.bookedY,
        [`Cancelled ${yearX}`]: row.cancelledX,
        [`Cancelled ${yearY}`]: row.cancelledY,
        [`ESTIMATED ${yearX}`]: row.estX,
        [`ESTIMATED ${yearY}`]: row.estY,
        [`FINAL ${yearX}`]: row.realX,
        [`FINAL ${yearY}`]: row.realY,
        'ESTIMATED DIFF': row.diffEst,
        'FINAL DIFF': row.diffReal,
        MAT: row.materializationPct != null ? Math.round(row.materializationPct) : '',
      };
    });

    if (sortedData.length >= 2) {
      tableData.push({
        Hotel: 'TOTAL',
        'Create Date': '',
        [`GROUPS ${yearX}`]: totals.bookedX,
        [`GROUPS ${yearY}`]: totals.bookedY,
        [`Cancelled ${yearX}`]: totals.cancelledX,
        [`Cancelled ${yearY}`]: totals.cancelledY,
        [`ESTIMATED ${yearX}`]: totals.estX,
        [`ESTIMATED ${yearY}`]: totals.estY,
        [`FINAL ${yearX}`]: totals.realX,
        [`FINAL ${yearY}`]: totals.realY,
        'ESTIMATED DIFF': totals.diffEst,
        'FINAL DIFF': totals.diffReal,
        MAT: totalMaterializationPct != null ? Math.round(totalMaterializationPct) : '',
      });
    }

    return tableData;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="mx-auto w-full min-w-0 max-w-[90rem] py-6 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="page-title">Hotels Performance</h1>
          <div className="flex items-center space-x-4">
            <ExportButton
              reportName="Hotels Performance"
              data={data}
              filters={{
                selectedCreationYears: appliedCreationYears,
                selectedArrivalYears: appliedArrivalYears,
                selectedManagers: appliedManagers,
                selectedSegments: appliedSegments,
                selectedClients: appliedClients,
                selectedHotels: appliedHotels,
                selectedHotelGroups: appliedHotelGroups,
                selectedArrivalMonths: appliedArrivalMonths,
                selectedArrivalQuarters: appliedArrivalQuarters,
                selectedCreationMonths: appliedCreationMonths,
                selectedCreationQuarters: appliedCreationQuarters,
                allClientsSelected,
                allHotelsSelected,
                allHotelGroupsSelected
              }}
              tableDataTransformer={getTableDataForExport}
            />
            <CacheStatus />
          </div>
        </div>
        <div className="mb-6">
          <FilterPanel
            selectedCreationYears={pendingCreationYears}
            selectedArrivalYears={pendingArrivalYears}
            selectedManagers={pendingManagers}
            selectedSegments={pendingSegments}
            selectedClients={pendingClients}
            selectedHotels={pendingHotels}
            selectedHotelGroups={pendingHotelGroups}
            availableCreationYears={availableCreationYears}
            availableArrivalYears={availableArrivalYears}
            availableManagers={availableManagers}
            availableSegments={availableSegments}
            clients={clients}
            hotels={hotels}
            hotelGroups={hotelGroups}
            setSelectedCreationYears={setPendingCreationYears}
            setSelectedArrivalYears={setPendingArrivalYears}
            setSelectedManagers={setPendingManagers}
            setSelectedSegments={setPendingSegments}
            resetFilters={resetFilters}
            selectedArrivalMonths={pendingArrivalMonths}
            selectedArrivalQuarters={pendingArrivalQuarters}
            setSelectedArrivalMonths={setPendingArrivalMonths}
            setSelectedArrivalQuarters={setPendingArrivalQuarters}
            selectedCreationMonths={pendingCreationMonths}
            selectedCreationQuarters={pendingCreationQuarters}
            setSelectedCreationMonths={setPendingCreationMonths}
            setSelectedCreationQuarters={setPendingCreationQuarters}
            onClientsChange={handleClientsChange}
            allClientsSelected={allClientsSelected}
            onHotelsChange={handleHotelsChange}
            allHotelsSelected={allHotelsSelected}
            onHotelGroupsChange={handleHotelGroupsChange}
            allHotelGroupsSelected={allHotelGroupsSelected}
            onApplyFilters={handleApplyFilters}
            hasUnappliedChanges={hasUnappliedChanges}
            selectedMarkets={pendingMarkets}
            markets={markets}
            allMarketsSelected={allMarketsSelected}
            onMarketsChange={handleMarketsChange}
          />
        </div>
        {loadingProgress && (
          <div className="mb-4 bg-blue-50 text-blue-700 p-2 rounded-md text-center">
            {loadingProgress}
          </div>
        )}
        <div className="flex gap-4 mb-4 items-end justify-between">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 w-full"
            />
          </div>
          <div className="flex flex-wrap gap-4 items-end justify-end">
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-700">Hotel Creation</span>
                <select
                  value={hotelCreationPeriod}
                  onChange={(e) => setHotelCreationPeriod(e.target.value as HotelCreationPeriod)}
                  className="border rounded px-2 py-1 text-sm"
                  aria-label="Hotel creation period"
                >
                  <option value="this_month">This month</option>
                  <option value="this_year">This year</option>
                  <option value="last_year">Last year</option>
                  <option value="all_time">All time</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              {hotelCreationPeriod === 'custom' && (
                <>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-700">From</span>
                    <input
                      type="date"
                      value={customCreationFrom}
                      onChange={(e) => setCustomCreationFrom(e.target.value)}
                      className="border rounded px-2 py-1 text-sm"
                      aria-label="Hotel creation from date"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gray-700">To</span>
                    <input
                      type="date"
                      value={customCreationTo}
                      onChange={(e) => setCustomCreationTo(e.target.value)}
                      className="border rounded px-2 py-1 text-sm"
                      aria-label="Hotel creation to date"
                    />
                  </div>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-700">Arrival Year X</span>
              <select value={yearX} onChange={e => setYearX(Number(e.target.value))} className="border rounded px-2 py-1" aria-label="Arrival year X">
                {availableArrivalYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-700">Arrival Year Y</span>
              <select value={yearY} onChange={e => setYearY(Number(e.target.value))} className="border rounded px-2 py-1" aria-label="Arrival year Y">
                {availableArrivalYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="w-full min-w-0 overflow-x-auto lg:overflow-x-visible">
            <table className="w-full min-w-[min(100%,52rem)] table-fixed border-collapse divide-y divide-gray-200 lg:min-w-0">
              <colgroup>
                <col style={{ width: nameColumnWidth }} />
                <col style={{ width: dateColumnWidth }} />
                <col style={{ width: groupsColumnWidth }} />
                <col style={{ width: groupsColumnWidth }} />
                <col style={{ width: cancelledColumnWidth }} />
                <col style={{ width: cancelledColumnWidth }} />
                <col style={{ width: revenueColumnWidth }} />
                <col style={{ width: revenueColumnWidth }} />
                <col style={{ width: revenueColumnWidth }} />
                <col style={{ width: revenueColumnWidth }} />
                <col style={{ width: diffColumnWidth }} />
                <col style={{ width: diffColumnWidth }} />
                <col style={{ width: pctColumnWidth }} />
              </colgroup>
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className={thClassLeft}
                    onClick={() => handleSort('venue')}
                  >
                    <div className="flex items-center gap-1">
                      Hotel
                      {sortConfig?.key === 'venue' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className={thClassLeft}
                    onClick={() => handleSort('createDateDisplay')}
                  >
                    <div className="flex items-center gap-1">
                      Create Date
                      {sortConfig?.key === 'createDateDisplay' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className={thClass}
                    onClick={() => handleSort('bookedX')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      GROUPS {yearX}
                      {sortConfig?.key === 'bookedX' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className={thClass}
                    onClick={() => handleSort('bookedY')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      GROUPS {yearY}
                      {sortConfig?.key === 'bookedY' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className={thClass}
                    onClick={() => handleSort('cancelledX')}
                  >
                    <div className="flex flex-col items-center justify-center gap-0.5 text-center leading-tight">
                      <span>CANCELLED</span>
                      <span className="inline-flex items-center gap-0.5">
                        {yearX}
                        {sortConfig?.key === 'cancelledX' && (
                          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </span>
                    </div>
                  </th>
                  <th
                    className={thClass}
                    onClick={() => handleSort('cancelledY')}
                  >
                    <div className="flex flex-col items-center justify-center gap-0.5 text-center leading-tight">
                      <span>CANCELLED</span>
                      <span className="inline-flex items-center gap-0.5">
                        {yearY}
                        {sortConfig?.key === 'cancelledY' && (
                          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </span>
                    </div>
                  </th>
                  <th
                    className={thClass}
                    onClick={() => handleSort('estX')}
                  >
                    <div className="flex flex-col items-center justify-center gap-0.5 text-center leading-tight">
                      <span>ESTIMATED</span>
                      <span className="inline-flex items-center gap-0.5">
                        {yearX}
                        {sortConfig?.key === 'estX' && (
                          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </span>
                    </div>
                  </th>
                  <th
                    className={thClass}
                    onClick={() => handleSort('estY')}
                  >
                    <div className="flex flex-col items-center justify-center gap-0.5 text-center leading-tight">
                      <span>ESTIMATED</span>
                      <span className="inline-flex items-center gap-0.5">
                        {yearY}
                        {sortConfig?.key === 'estY' && (
                          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </span>
                    </div>
                  </th>
                  <th
                    className={thClass}
                    onClick={() => handleSort('realX')}
                  >
                    <div className="flex flex-col items-center justify-center gap-0.5 text-center leading-tight">
                      <span>FINAL</span>
                      <span className="inline-flex items-center gap-0.5">
                        {yearX}
                        {sortConfig?.key === 'realX' && (
                          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </span>
                    </div>
                  </th>
                  <th
                    className={thClass}
                    onClick={() => handleSort('realY')}
                  >
                    <div className="flex flex-col items-center justify-center gap-0.5 text-center leading-tight">
                      <span>FINAL</span>
                      <span className="inline-flex items-center gap-0.5">
                        {yearY}
                        {sortConfig?.key === 'realY' && (
                          <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </span>
                    </div>
                  </th>
                  <th
                    className={thClass}
                    onClick={() => handleSort('diffEst')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      ESTIMATED DIFF
                      {sortConfig?.key === 'diffEst' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className={thClass}
                    onClick={() => handleSort('diffReal')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      FINAL DIFF
                      {sortConfig?.key === 'diffReal' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th
                    className={thClass}
                    onClick={() => handleSort('materializationPct')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      MAT.
                      {sortConfig?.key === 'materializationPct' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedData.map((row) => {
                  const rawDate = row.hotelId != null ? hotelRawDateById.get(row.hotelId) : undefined;
                  const createLabel = formatHotelCreatedLabel(rawDate);
                  return (
                    <tr key={row.rowKey} className="hover:bg-gray-50">
                      <td className={tdHotel}>
                        <div>{row.venue}</div>
                        <div className="mt-0.5 text-[10px] font-medium text-blue-600 lg:text-[11px]">
                          {row.hotelGroupLabel}
                        </div>
                      </td>
                      <td className={`${tdNum} text-left text-gray-700`}>{createLabel}</td>
                      <td className={`${tdNum} text-center`}>{row.bookedX}</td>
                      <td className={`${tdNum} text-center`}>{row.bookedY}</td>
                      <td className={`${tdNum} text-center`}>{row.cancelledX}</td>
                      <td className={`${tdNum} text-center`}>{row.cancelledY}</td>
                      <td className={`${tdNum} text-right`}>{formatCurrency(row.estX)}</td>
                      <td className={`${tdNum} text-right`}>{formatCurrency(row.estY)}</td>
                      <td className={`${tdNum} text-right`}>{formatCurrency(row.realX)}</td>
                      <td className={`${tdNum} text-right`}>{formatCurrency(row.realY)}</td>
                      <td className={`${tdNum} text-right`}>{formatCurrency(row.diffEst)}</td>
                      <td className={`${tdNum} text-right`}>{formatCurrency(row.diffReal)}</td>
                      <td className={`${tdNum} text-center`}>
                        {row.materializationPct != null ? (
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-xs font-semibold text-center ${materializationBadgeClass(
                              row.materializationPct,
                            )}`}
                          >
                            {formatMaterializationPct(row.materializationPct)}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {sortedData.length >= 2 && (
                  <tr className="bg-gray-50 border-t-2 border-gray-200 font-bold">
                    <td className="px-1.5 py-2 align-top text-[11px] font-bold leading-snug text-gray-900 lg:text-xs">TOTAL</td>
                    <td className={`${tdNum} text-left text-gray-500`}>—</td>
                    <td className={`${tdNum} text-center`}>{totals.bookedX}</td>
                    <td className={`${tdNum} text-center`}>{totals.bookedY}</td>
                    <td className={`${tdNum} text-center`}>{totals.cancelledX}</td>
                    <td className={`${tdNum} text-center`}>{totals.cancelledY}</td>
                    <td className={`${tdNum} text-right`}>{formatCurrency(totals.estX)}</td>
                    <td className={`${tdNum} text-right`}>{formatCurrency(totals.estY)}</td>
                    <td className={`${tdNum} text-right`}>{formatCurrency(totals.realX)}</td>
                    <td className={`${tdNum} text-right`}>{formatCurrency(totals.realY)}</td>
                    <td className={`${tdNum} text-right`}>{formatCurrency(totals.diffEst)}</td>
                    <td className={`${tdNum} text-right`}>{formatCurrency(totals.diffReal)}</td>
                    <td className={`${tdNum} text-center`}>
                      {totalMaterializationPct != null ? (
                        <span
                          className={`inline-block px-2 py-0.5 rounded text-xs font-semibold text-center ${materializationBadgeClass(
                            totalMaterializationPct,
                          )}`}
                        >
                          {formatMaterializationPct(totalMaterializationPct)}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
